import { useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSalvcarContext } from '@/app/contexts/SalvcarContext'
import { useCustomerCpf } from '@/app/hooks/useCustomerCpf'
import { useGetTransactionCommission } from '@/app/hooks/useGetTransactionCommission'
import { validateCpf } from '@/app/utils/cpfFormater'
import { useRegisterSupportContext } from '@/app/store/supportScreenContext'
import { PartnerType } from '@/app/utils/partners.enum'

const getPersonalInfoFormSchema = z.object({
  cpf: z
    .string()
    .min(11, 'CPF é obrigatório')
    .refine((v) => validateCpf(v), 'CPF Inválido')
    .transform((f) => f.replace(/\D/g, '')),
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().min(1, 'E-mail é obrigatório'),
  phone: z.string().min(1, 'Telefone é obrigatório'),
})

type getPersonalInfoFormSchemaData = z.infer<typeof getPersonalInfoFormSchema>

interface usePersonalInfoFormModalProps {
  onContinue?: (data: getPersonalInfoFormSchemaData) => void
}

export default function usePersonalInfoFormModal({
  onContinue,
}: usePersonalInfoFormModalProps) {
  const getPersonalInfoForm = useForm<getPersonalInfoFormSchemaData>({
    resolver: zodResolver(getPersonalInfoFormSchema),
    mode: 'onChange',
  })

  const { customerData, setCustomerData } = useSalvcarContext()

  const { watch, setValue, handleSubmit } = getPersonalInfoForm

  const cpf = watch('cpf')
  const cpfClean = cpf?.replace(/\D/g, '')

  const { data: customer, isSuccess } = useCustomerCpf(cpfClean)

  const { data: commisionData, isLoading: isLoadingCommisionData } =
    useGetTransactionCommission({ type: 'SALVCAR' })

  useEffect(() => {
    if (isSuccess && customer) {
      setValue('name', customer.name)
      setValue('email', customer.email)
    }
  }, [isSuccess, customer, setValue])

  const submitModal = handleSubmit(async (formData) => {
    setCustomerData(formData)

    onContinue?.(formData)
  })

  useEffect(() => {
    if (customerData) {
      Object.entries(customerData).forEach(([key, value]) => {
        setValue(key as keyof typeof customerData, value)
      })
    }
  }, [customerData, setValue])

  useRegisterSupportContext('simulation.salvcar.personalInfo', {
    product: PartnerType.SALVCAR,
    step: 'Dados pessoais Salvcar',
    data: getPersonalInfoForm.watch(),
  })

  return {
    form: getPersonalInfoForm,
    commisionData,
    isLoadingCommisionData,
    submitModal,
  }
}
